import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { datesHelper } from "@/shared/utils";
import { wsfScheduleApiMeta } from "../apiMeta";
import {
  type ScheduleByTripDateAndTerminalsInput,
  scheduleByTripDateAndTerminals,
} from "./shared/schedules.input";
import { type Schedule, scheduleSchema } from "./shared/schedules.output";

/**
 * Metadata for the fetchScheduleByTripDateAndDepartingTerminalIdAndTerminalIds endpoint
 */
export const scheduleByTripDateAndDepartingTerminalIdAndTerminalIdsMeta = {
  functionName: "fetchScheduleByTripDateAndDepartingTerminalIdAndTerminalIds",
  endpoint: "/schedule/{TripDate}/{DepartingTerminalID}/{ArrivingTerminalID}",
  inputSchema: scheduleByTripDateAndTerminals,
  outputSchema: scheduleSchema,
  sampleParams: {
    TripDate: datesHelper.tomorrow(),
    DepartingTerminalID: 1,
    ArrivingTerminalID: 10,
  },
  endpointDescription:
    "Get sailing schedule for a terminal pair and trip date.",
} satisfies EndpointMeta<ScheduleByTripDateAndTerminalsInput, Schedule>;

/**
 * Fetch function for retrieving sailing schedule for a terminal pair and trip date
 */
const { fetch, useHook } = createFetchAndHook<
  ScheduleByTripDateAndTerminalsInput,
  Schedule
>({
  api: wsfScheduleApiMeta,
  endpoint: scheduleByTripDateAndDepartingTerminalIdAndTerminalIdsMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/schedules.endpoints").schedulesGroup.cacheStrategy,
});

/**
 * Fetch function for retrieving sailing schedule for a terminal pair and trip date
 */
export const fetchScheduleByTripDateAndDepartingTerminalIdAndTerminalIds: FetchFactory<
  ScheduleByTripDateAndTerminalsInput,
  Schedule
> = fetch;

/**
 * React Query hook for retrieving sailing schedule for a terminal pair and trip date
 */
export const useScheduleByTripDateAndDepartingTerminalIdAndTerminalIds: HookFactory<
  ScheduleByTripDateAndTerminalsInput,
  Schedule
> = useHook;
